<template>
  <div class="w-full h-full pt-5">
    <div class="flex">
      <h1 class="text-xl text-left font-extrabold pl-4 mr-8">Pension</h1>
      <Breadcrumb :items="breadcrumbs" />
    </div>
    <div v-if="!showError">
      <div style="height: 100%" v-if="loading">
        <loader size="xxs" :loader-image="false" />
      </div>

      <div v-else>
        <div class="mx-4 mt-3">
          <div class="w-full flex mt-2 gap-5">
            <Card
              class="w-1/3 text-white px-3 pt-6"
              style="background: #3454d1 !important"
            >
              <div class="flex">
                <Icon class-name="text-white " icon-name="union" size="l" />

                <div class="flex flex-col w-1/2 ml-4">
                  <span class="font-thin">Total pension deductions</span>
                  <span class="text-lg font-bold">
                    {{
                      convertToCurrency(pensionsData.totalPensionDeduction)
                    }}</span
                  >
                </div>

                <div class="border bg-white w-0 mx-4 my-4" />

                <div class="flex flex-col w-1/2 ml-4">
                  <div class="flex flex-col">
                    <span class="font-thin">Total Remitted</span>
                    <span class="text-lg font-bold">{{
                      convertToCurrency(pensionsData.totalRemitted)
                    }}</span>
                  </div>

                  <div class="flex flex-col mt-2">
                    <span class="font-thin">Total Outstanding</span>
                    <span class="text-lg font-bold">{{
                      convertToCurrency(pensionsData.totalOutstanding)
                    }}</span>
                  </div>
                </div>
              </div>
            </Card>

            <Card
              class="w-2/5 text-white px-3 flex"
              style="
                background: #321c3b !important;
                box-shadow: 0px 1px 2px rgba(45, 49, 66, 0.06),
                  0px 1px 3px rgba(8, 7, 8, 0.1);
                border-radius: 8px;
              "
            >
              <div class="flex">
                <Icon icon-name="payrunSheet" class="" size="l" />
                <div class="flex flex-col ml-2 my-8">
                  <span class="font-semibold text-xs uppercase"
                    >Total Employee Contributions</span
                  >
                  <span class="font-bold text-lg"
                    >{{
                      convertToCurrency(pensionsData.totalEmployeeContributions)
                    }}
                  </span>
                </div>
              </div>
              <div class="border bg-romanSilver w-0 mx-4 my-4" />
              <div class="flex">
                <Icon icon-name="wallet" class="" size="l" />
                <div class="flex flex-col ml-4 my-8">
                  <span class="font-semibold text-xs uppercase"
                    >Total Employer Contributions</span
                  >
                  <span class="font-bold text-lg">{{
                    convertToCurrency(pensionsData.totalEmployerContributions)
                  }}</span>
                </div>
              </div>
            </Card>

            <Card
              class="w-1/3 px-3 flex text-white"
              style="
                background: #13b56a !important;
                box-shadow: 0px 1px 2px rgba(45, 49, 66, 0.06),
                  0px 1px 3px rgba(8, 7, 8, 0.1);
                border-radius: 8px;
              "
            >
              <Icon icon-name="employee_payrun" class-name="ml-4" size="m" />

              <div class="flex flex-col my-8 ml-6">
                <span class="font-semibold uppercase text-xs"
                  >Pensionable employees</span
                >
                <span class="font-bold text-lg pt-2">{{
                  pensionsData.employeeCount
                }}</span>
              </div>
            </Card>
          </div>
          <div class="mt-2 w-full gap-5" v-if="payrunData.length > 0">
            <div class="w-full mt-4 gap-5">
              <Card class="flex flex-col w-full p-3">
                <h1 class="font-bold text-lg my-4">Pension Summary</h1>
                <div class="w-full flex justify-between">
                  <span class="w-4/6 text-base"
                    >This is an overview of your employee pension summary.</span
                  >

                  <Card
                    class="
                      p-2
                      h-10
                      flex
                      justify-center
                      items-center
                      cursor-
                      W-2/6
                    "
                  >
                    <Icon
                      class-name="text-darkPurple mx-auto self-center mr-2"
                      size="xs"
                      icon-name="calendar"
                    />
                    | <span class="ml-2 text-base">Year to Date</span>
                    <Icon
                      class-name="text-darkPurple mx-auto self-center ml-2"
                      size="xms"
                      icon-name="caret-down"
                    />
                  </Card>
                </div>
              </Card>
            </div>

            <div class="w-full mt-10">
              <sTable
                :headers="pensionsHeader"
                :items="payrunData"
                aria-label="pension table"
                class="w-full mt-1"
                :has-checkbox="false"
                :loading="isLoader"
                :pagination-list="metaData"
                @page="handlePage($event)"
                @itemsPerPage="handleItemsPerPage($event)"
                page-sync
                has-number
              >
                <template v-slot:item="{ item }">
                  <div v-if="item.status">
                    <Badge
                      label="Remitted"
                      variant="primary"
                      background-color="rgba(19, 181, 106, 0.08)"
                      color="#13B56A"
                      style="
                        padding: 8px;
                        height: 33px;
                        font-weight: 600;
                        font-size: 14px;
                      "
                      v-if="item.data.status === 'paid'"
                    />
                    <Badge
                      label="Outstanding"
                      variant="primary"
                      background-color="rgba(234, 60, 83, 0.08)"
                      color="#EA3C53"
                      style="
                        padding: 8px;
                        height: 33px;
                        font-weight: 600;
                        font-size: 14px;
                      "
                      v-else
                    />
                  </div>
                  <div v-else-if="item.id">
                    <div
                      @click="handleView(item.data.id)"
                      class="cursor-pointer h-8 flex"
                    >
                      <icon
                        icon-name="icon-eye"
                        size="xsm"
                        class="-mt-1 -ml-2 text-blueCrayola"
                        style="padding: 3px 10px; width: 36px; height: 36px"
                      />
                    </div>
                  </div>
                </template>
              </sTable>
            </div>
          </div>

          <div v-else class="mt-10">
            <div
              class="w-full h-auto flex flex-col"
              style="align-items: center"
            >
              <div
                class="w-5/12 h-auto mt-5 mb-5 flex"
                style="justify-content: center"
              >
                <icon
                  icon-name="illustration_approval"
                  class-name="icon-Size mb-4 mt-4 text-white"
                  size="l"
                  style="width: 50%"
                />
              </div>
              <div
                class="w-6/12 h-auto text-center"
                style="width: 610px; line-height: 19px"
              >
                <p class="font-black text-lg text-flame">No Data Available</p>
                <p class="mt-5 mb-5 tracking-wide">
                  There is no data to show currently.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-center items-center" v-else>
      <error-page />
    </div>
  </div>
</template>

<script>
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import STable from "@scelloo/cloudenly-ui/src/components/table";
import ErrorPage from "@/modules/Admin/error403";
import Badge from "@scelloo/cloudenly-ui/src/components/badge";
import Card from "@/components/Card";
import Icon from "@/components/Icon";

export default {
  name: "Pensions",
  components: {
    Card,
    Badge,
    Breadcrumb,
    ErrorPage,
    Icon,
    STable,
  },
  data() {
    return {
      breadcrumbs: [
        {
          disabled: false,
          text: "Payroll",
          href: "Payroll",
          id: "Payroll",
        },
        {
          disabled: false,
          text: "Compliance",
          href: "Compliance",
          id: "Compliance",
        },
        {
          disabled: false,
          text: "Pension",
          href: "Pension",
          id: "Pension",
        },
      ],
      showError: false,
      pensionsHeader: [
        { title: "Payrun Cycle", value: "payrunCycle" },
        { title: "Payrun Date", value: "payrunDate" },
        { title: "Payrun Id No", value: "idNo" },
        { title: "No of Employees", value: "employeesCount" },
        { title: "Employee Deductions", value: "employeesDeductions" },
        { title: "Employer Contributions", value: "employersContributions" },
        { title: "Total Pension", value: "totalPension" },
        { title: "Status", value: "status" },
        { title: "", value: "id", image: true },
      ],
      metaData: {},
      pageNumber: null,
      itemsPerPage: null,
      loading: true,
      isLoader: true,
      payrunData: [],
      pensionsData: {
        totalPensionDeduction: 0,
        totalEmployeeContributions: 0,
        totalEmployerContributions: 0,
        totalRemitted: 0,
        totalOutstanding: 0,
        employeeCount: 0,
      },
    };
  },
  methods: {
    async handleView(id) {
      try {
        await this.$handlePrivilege("pension", "viewPayCycleDetails");
        this.$router.push({
          name: "ViewPension",
          params: { id },
        });
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },
    resetDate(value) {
      if (value) {
        const dateValue = value;

        const split = dateValue.split("T00:00:00.000Z");

        return `${split[0]}T13:00:00.000Z`;
      }
      return "";
    },

    handlePage(pageNumber) {
      this.pageNumber = pageNumber;
      this.queryUpdate();
    },

    handleItemsPerPage(itemNo) {
      this.itemsPerPage = itemNo;
      this.queryUpdate();
    },

    queryUpdate() {
      const pageNumber = this.pageNumber
        ? `?page=${this.pageNumber}`
        : `?page=${this.metaData.page}`;
      const itemPage = this.itemsPerPage ? `&perPage=${this.itemsPerPage}` : "";
      const filterQuery = `${pageNumber}${itemPage}`;
      this.isLoader = true;
      this.getPension(filterQuery);
    },

    getPension(filters) {
      this.$_getPayrollPensionSummary(filters)
        .then((response) => {
          const payrunData = response.data.payruns;
          this.pensionsData.totalPensionDeduction =
            response.data.summary.totalPensionDeduction;
          this.pensionsData.totalEmployeeContributions =
            response.data.summary.totalEmployeeContributions;
          this.pensionsData.totalEmployerContributions =
            response.data.summary.totalEmployerContributions;
          this.pensionsData.totalRemitted = response.data.summary.totalRemitted;
          this.pensionsData.totalOutstanding =
            response.data.summary.totalOutstanding;
          this.pensionsData.employeeCount = response.data.summary.employeeCount;

          this.metaData = response.data.meta;

          this.payrunData = payrunData.map((data) => ({
            id: data.id,
            payrunCycle: `${this.$DATEFORMAT(
              new Date(`${data.year}-${data.month}-01`),
              `MMMM yyyy`
            )} Cycle`,
            payrunDate: data.paidAt
              ? `${this.$DATEFORMAT(
                  new Date(`${data.paidAt}`),
                  `MMMM dd, yyyy`
                )}`
              : `${this.$DATEFORMAT(
                  new Date(this.resetDate(`${data.payDate}`)),
                  `MMMM dd, yyyy`
                )}`,
            idNo: data.payrunNo,
            employeesCount: data.employeesCount,
            employeesDeductions: `${this.convertToCurrency(
              data.employeeContribution
            )}`,
            employersContributions: `${this.convertToCurrency(
              data.employerContribution
            )}`,
            totalPension: `${this.convertToCurrency(data.totalPensionAmount)}`,
            status: `${data.pensionStatus}`,
          }));

          this.loading = false;
          this.isLoader = false;
        })
        .catch(() => {
          this.loading = false;
          this.isLoader = false;
        });
    },
  },
  async mounted() {
    try {
      await this.$handlePrivilege("pension", "viewRegister");
      this.showError = false;
      this.getPension("");
    } catch (error) {
      this.showError = true;
    }
  },
};
</script>

<style scoped>
.icon {
  width: 24px;
  height: 24px;
  padding: 3px;
}
</style>
